import React from 'react';
import { IOrdersInventoryBal } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';

interface Props {
  currData?: IOrdersInventoryBal;
  matPrice: number;
  unit: IMassUnitObject;
  isCoverage: boolean;
}

export const LogisticsStock: React.FC<Props> = ({ currData, matPrice, isCoverage }) => {
  if (!currData) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md items-center p-3">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  // Calculate the maximum value for scaling
  const maxValue = isCoverage
    ? Math.max(currData.currCover, currData.expectedDemand, currData.reqCover) * 1.2
    : Math.max(currData.inventory, currData.futureOrders, currData.requiredQuantity) * 1.2;
  if (!maxValue || maxValue < 2) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md items-center p-3">
        <div className="text-gray-500">Choose another material</div>
      </div>
    );
  }
  const items = isCoverage
    ? [
        { label: 'Current coverage', value: currData.currCover, width: (currData.currCover / maxValue) * 100 },
        { label: 'Expected demand', value: currData.expectedDemand, width: (currData.expectedDemand / maxValue) * 100 },
        { label: 'Required coverage', value: currData.reqCover, width: (currData.reqCover / maxValue) * 100 },
      ]
    : [
        { label: 'Current inventory', value: currData.inventory, width: (currData.inventory / maxValue) * 100 },
        {
          label: 'Expected consumption (MRP)',
          value: currData.futureOrders,
          width: (currData.futureOrders / maxValue) * 100,
        },
        {
          label: 'Required amount',
          value: currData.requiredQuantity,
          width: (currData.requiredQuantity / maxValue) * 100,
        },
      ];
  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md items-center p-3 rounded-lg">
      <h3 className="w-full text-lg font-semibold py-2 flex items-center gap-2">
        <span>{isCoverage ? 'Total coverage ratio' : 'Total logistics'}</span>
        <i className="fas fa-info-circle text-gray-400 text-sm" />
      </h3>

      {/* Chart Area */}
      <div className="w-full flex flex-col gap-2 ">
        {/* Inventory Bar */}
        <div className="w-full border-l border-b border-gray-300 ">
          <div className="text-sm text-gray-600 mb-2 pl-1">{items[0].label}</div>

          <div className="relative h-8 rounded bg-white">
            {/* Inventory segment */}

            <div className="absolute h-full bg-blue-400 rounded-r" style={{ width: `${items[0].width}%` }} />
            {/* Required quantity indicator line */}
            <div className="absolute h-full w-1 bg-red-500 " style={{ left: `${items[2].width}%` }} />
          </div>
          <div>
            {/* Expected Inventory MRP Bar */}
            <div className="flex flex-col gap-2 w-full my-3">
              <div className="text-sm text-gray-600 pl-1">{items[1].label}</div>
              <div className="relative h-8 rounded bg-gray-75">
                {/* Expected inventory outlin */}

                <div
                  className="absolute h-full border-y-2 border-r-2 border-dashed border-gray-400 rounded-r bg-gray-100"
                  style={{ width: `${items[1].width}%` }}
                />
                {/* Required quantity indicator line */}
                <div className="absolute h-full w-1 bg-red-500 z-10" style={{ left: `${items[2].width}%` }} />
              </div>
            </div>
          </div>
        </div>
        {/* X-axis */}
        <div className="w-full mb-4">
          <div className="flex justify-between text-xs text-gray-500">
            {Array.from({ length: 6 }, (_, i) => Math.round((maxValue / 5) * i)).map((value, idx) => (
              <span key={idx}>{value.toFixed(0)}</span>
            ))}
          </div>
          <div className="text-sm flex justify-center text-gray-600 mt-1">Metric tons</div>
        </div>
      </div>
      {/* Legend */}
      <div className="w-full space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-400 rounded"></div>
          <span className="text-sm">
            {items[0].label + ' '}
            <strong>
              {items[0].value.toFixed(0)}mt (${(items[0].value * matPrice).toLocaleString()})
            </strong>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-dashed border-gray-400 rounded"></div>
          <span className="text-sm">
            {items[1].label}:{' '}
            <strong>
              {items[1].value.toFixed(0)}mt ($
              {(items[1].value * matPrice).toLocaleString()})
            </strong>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-sm">
            {items[2].label}:{' '}
            <strong>
              {items[2].value.toFixed(0)} mt ($
              {(items[2].value * matPrice).toLocaleString()})
            </strong>
          </span>
        </div>
      </div>
    </div>
  );
};
